import _ from 'lodash';
import * as moment from 'moment';
import {
  BracketFormat,
  BracketPosition,
  BracketVisibility,
  IEventBracketMatchDetails,
  IEventBracketMatchGameDetails,
  IEventBracketSummary,
  IPublishedBracketSummary,
  MatchGameStatus
} from '../../../../../types/eventBracketTypes';
import { getMatchByMode } from '../../../event-management/components/brackets/matches/utils';
import {
  ActiveGameMatchMode,
  TDynamicEventBracketDetailsState,
  TDynamicEventBracketStateItem
} from '../../store/details/types';

export interface IMatchPosition {
  round: number;
  match: number;
  position: BracketPosition;
}

export const getMatchElementId = (position: IMatchPosition) => {
  return `round-${position.round}-match-${position.match}-${position.position}`;
};

export const getRoundElementId = (round: number, position: BracketPosition) => {
  return `round-${round}-${position}`;
};

export const calculateDistanceBetweenMatchConnections = (
  firstMatchPosition: IMatchPosition,
  secondMatchPosition: IMatchPosition
): number | null => {
  const firstMatch = document.getElementById(getMatchElementId(firstMatchPosition));
  const secondMatch = document.getElementById(getMatchElementId(secondMatchPosition));
  if (!firstMatch || !secondMatch) {
    return null;
  }

  const firstMatchConnection = firstMatch.querySelector('.connections-container');
  const secondMatchConnection = secondMatch.querySelector('.connections-container');
  if (!firstMatchConnection || !secondMatchConnection) {
    return null;
  }

  return secondMatchConnection.getBoundingClientRect().y - firstMatchConnection.getBoundingClientRect().y;
};

export const getBracketsToPublicView = (brackets: IEventBracketSummary[]) => {
  return brackets
    .filter(
      (bracket) =>
        ![BracketFormat.TIME_TRIALS, BracketFormat.STAGE].includes(bracket.format) &&
        [BracketVisibility.FULL_VISIBILITY, BracketVisibility.BRACKET_ONLY].includes(bracket.visibility)
    )
    .sort((a, b) => moment.utc(a.bracketStartsDate).diff(moment.utc(b.bracketStartsDate)));
};

export const DEFAULT_DYNAMIC_EVENT_STATE: TDynamicEventBracketStateItem = {
  isLoading: false,
  isLoaded: false,
  brackets: {},
  event: null
};

export const DEFAULT_DYNAMIC_BRACKET_DETAILS_STATE: TDynamicEventBracketDetailsState = {
  isLoading: false,
  isLoaded: false,
  bracket: null
};

export const MIN_BRACKET_SIZE = 200;
export const MAX_BRACKET_SIZE = 2560;
export const MAX_BRACKET_SCALE = 1.25;
export const MIN_BRACKET_SCALE = 0.15;

export const getActiveGameMatchInBracket = (
  bracket: IPublishedBracketSummary,
  mode: ActiveGameMatchMode = ActiveGameMatchMode.Latest
): {
  match: IEventBracketMatchDetails;
  game?: IEventBracketMatchGameDetails;
} | null => {
  const allMatches = bracket
    ? _.flatMap(bracket.segments, (segment) => _.flatMap(segment?.rounds, (round) => _.values(round)))
    : [];

  if (bracket?.tieBreakerMatches) {
    allMatches.push(...bracket.tieBreakerMatches);
  }

  const mostRelevantMatch = getMatchByMode(allMatches, mode);

  if (!mostRelevantMatch) {
    return null;
  }

  const activeGame =
    mostRelevantMatch.games!.find((game) => game.status === MatchGameStatus.IN_PROGRESS) ||
    mostRelevantMatch.games!.filter((game) => game.status === MatchGameStatus.COMPLETE).pop();

  return {
    match: mostRelevantMatch,
    game: activeGame
  };
};

export const doesBracketMatchGameHavePreviewData = (
  match: IEventBracketMatchDetails,
  game: IEventBracketMatchGameDetails
) => {
  if (!_.isEmpty(game.firstTeamRoster) || !_.isEmpty(game.secondTeamRoster)) {
    return true;
  }

  if (game.videoId) {
    return true;
  }

  return false;
};
